import { createConsumer } from "@rails/actioncable";
import { useTenantStore } from "@/stores/tenantStore.js";

// Singleton to track our authenticated consumer
let authenticatedConsumerInstance = null;

// Function to get current tenant-aware WebSocket URL
function getCurrentWebSocketUrl() {
  try {
    const tenantStore = useTenantStore();
    // Use WebSocket URL from tenant store if available
    if (tenantStore.websocketUrl) {
      let wsUrl = tenantStore.websocketUrl;

      // In development, replace subdomain with localhost for actual connection
      if (window.location.hostname.includes("localhost")) {
        // Replace rheden-api.localhost:3000 with localhost:3000 while preserving protocol
        wsUrl = wsUrl.replace(
          /(ws|wss):\/\/[^.]+\.localhost:(\d+)/,
          "$1://localhost:$2",
        );
        console.log(
          "🔌 Development: Modified WebSocket URL for localhost:",
          wsUrl,
        );
      } else {
        console.log("🔌 Using WebSocket URL from tenant store:", wsUrl);
      }

      return wsUrl;
    }
  } catch (error) {
    console.warn(
      "Could not access tenant store for WebSocket URL, using fallback",
    );
  }

  // Fallback: construct from current location
  // Use ws:// for localhost, wss:// for everything else
  const wsProtocol = window.location.hostname.includes("localhost")
    ? "ws:"
    : "wss:";
  const fallbackUrl = `${wsProtocol}//${window.location.host}/cable`;
  console.log("🔌 Using fallback WebSocket URL:", fallbackUrl);
  return fallbackUrl;
}

// Function to create an authenticated consumer (or return existing one)
export function createAuthenticatedConsumer(token) {
  // If we already have an authenticated consumer, return it
  if (authenticatedConsumerInstance) {
    console.log("Reusing existing authenticated ActionCable connection");
    return authenticatedConsumerInstance;
  }

  // Get dynamic WebSocket URL based on current tenant
  const wsUrl = getCurrentWebSocketUrl();
  const tenantStore = useTenantStore();
  const subdomain = tenantStore?.tenant?.subdomain || "";

  // Add token and subdomain to the URL as parameters (helps tenant resolution on localhost)
  const paramJoin = wsUrl.includes("?") ? "&" : "?";
  const authenticatedUrl = `${wsUrl}${paramJoin}token=${encodeURIComponent(token)}&subdomain=${encodeURIComponent(subdomain)}`;
  console.log(
    "Creating authenticated ActionCable connection:",
    authenticatedUrl,
  );

  // Create a new consumer and store it in our singleton
  authenticatedConsumerInstance = createConsumer(authenticatedUrl);
  return authenticatedConsumerInstance;
}

// Singleton to track our default (unauthenticated) consumer
let defaultConsumerInstance = null;

// Function to get default consumer (created on-demand)
function getDefaultConsumer() {
  if (!defaultConsumerInstance) {
    const wsUrl = getCurrentWebSocketUrl();
    const tenantStore = useTenantStore();
    const subdomain = tenantStore?.tenant?.subdomain || "";
    const paramJoin = wsUrl.includes("?") ? "&" : "?";
    const urlWithSub = `${wsUrl}${paramJoin}subdomain=${encodeURIComponent(subdomain)}`;
    console.log("🔌 Creating default ActionCable connection:", urlWithSub);
    defaultConsumerInstance = createConsumer(urlWithSub);
  }
  return defaultConsumerInstance;
}

// Function to reset the authenticated consumer (for testing or logout)
export function resetAuthenticatedConsumer() {
  if (authenticatedConsumerInstance) {
    console.log("Disconnecting authenticated ActionCable connection");
    try {
      authenticatedConsumerInstance.disconnect();
    } catch (error) {
      console.error("Error disconnecting authenticated consumer:", error);
    }
    authenticatedConsumerInstance = null;
  }
}

// Function to reset the default consumer (when tenant changes)
export function resetDefaultConsumer() {
  if (defaultConsumerInstance) {
    console.log("Disconnecting default ActionCable connection");
    try {
      defaultConsumerInstance.disconnect();
    } catch (error) {
      console.error("Error disconnecting default consumer:", error);
    }
    defaultConsumerInstance = null;
  }
}

// Function to reset all consumers (for tenant changes or logout)
export function resetAllConsumers() {
  resetAuthenticatedConsumer();
  resetDefaultConsumer();
}

export default getDefaultConsumer;
